/* eslint-disable @typescript-eslint/ban-ts-comment */
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';

export const isShallowCopy = <T>(prev: T, next: T): boolean => {
    // eslint-disable-next-line eqeqeq
    if (prev === null && next === null) {
        // When both null then objects are equal
        return true;
    }

    // eslint-disable-next-line eqeqeq
    if (prev === null || next === null) {
        // When one is null then objects are unequal
        return false;
    }

    if (prev === undefined && next === undefined) {
        // When both undefined then objects are equal
        return true;
    }

    if (prev === undefined || next === undefined) {
        // When one is undefined then objects are unequal
        return false;
    }

    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (prevKeys.length !== nextKeys.length) {
        // If number of keys is different: unequal
        return false;
    }
    for (const key of nextKeys) {
        //below ignore was added to get rid of suppressImplicitAnyIndexErrors in our tsconfig, at least it's limited to this usage now.
        //@ts-ignore
        if (prev[key] !== next[key]) {
            // One key is different: unequal
            return false;
        }
    }

    return true; // Keys are the same: equal
};

export const calcDiff = function <T extends string | number>(prev: T[], next: T[]): { added: T[]; removed: T[] } {
    if ((!prev && !next) || (prev.length === 0 && next.length === 0)) {
        return { removed: [], added: [] };
    }
    if (!prev || prev.length === 0) {
        return { removed: [], added: [...next] };
    }
    if (!next || next.length === 0) {
        return { removed: [...prev], added: [] };
    }

    const removed = new Map(prev.map((item) => [item, true]));
    const added = new Map(next.map((item) => [item, true]));
    for (const [item] of removed) {
        if (added.has(item)) {
            removed.set(item, false);
        }
    }
    for (const [item] of added) {
        if (removed.has(item)) {
            added.set(item, false);
        }
    }

    return {
        removed: [...removed.entries()].filter((e) => !!e[1]).map((e) => e[0]),
        added: [...added.entries()].filter((e) => !!e[1]).map((e) => e[0]),
    };
};

export const toDictionary = function <T, TKey extends string | number, TValue>(
    list: T[] | undefined,
    keySelector: (item: T) => TKey,
    valueSelector: (item: T, index: number) => TValue,
): StringDictionary<TValue> {
    const result: StringDictionary<TValue> = {};

    if (!list) {
        return {};
    }

    for (let i = 0; i < list.length; i++) {
        const item = list[i];
        const key = keySelector(item);

        if (key in result) {
            console.warn('Item with the same key has already been added');
            continue;
        }

        result[key] = valueSelector(item, i);
    }

    return result;
};
